import { useEffect, useState } from 'react';
import { History, Workflow, ChevronRight } from 'lucide-react';
import { useAgentStudioNavigate } from '../lib/routes';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/ui/Card';
import { EmptyState, Skeleton } from '../components/ui/States';
import { RunStatusBadge } from '../components/StatusBadges';
import { runsService } from '../services/runsService';
import { formatRelativeTime, formatDuration } from '../lib/utils';
import type { Run } from '../types';

export function RunsListPage() {
  const navigate = useAgentStudioNavigate();
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    runsService.list().then((r) => {
      setRuns([...r].sort((a, b) => b.startedAt.localeCompare(a.startedAt)));
      setLoading(false);
    });
  }, []);

  return (
    <>
      <PageHeader title="Execuções" subtitle={`${runs.length} execuções registradas`} />
      <div className="flex-1 overflow-y-auto scrollbar-thin p-8">
        {loading ? (
          <Card>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 border-b border-line px-5 py-4 last:border-0">
                <Skeleton className="h-9 w-9 rounded-full" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-5 w-24" />
              </div>
            ))}
          </Card>
        ) : runs.length === 0 ? (
          <EmptyState icon={History} title="Nenhuma execução ainda" description="Execute um fluxo para ver o histórico aqui." />
        ) : (
          <Card>
            <div className="divide-y divide-line">
              {runs.map((run) => (
                <button
                  key={run.id}
                  onClick={() => navigate(`/runs/${run.id}`)}
                  className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-cloud"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-cloud">
                    <Workflow className="h-4 w-4 text-slate" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{run.flowName}</p>
                    <p className="text-xs text-slate">
                      {formatRelativeTime(run.startedAt)} · {formatDuration(run.durationMs)} · {run.events.length} eventos
                    </p>
                  </div>
                  <RunStatusBadge status={run.status} />
                  <ChevronRight className="h-4 w-4 text-slate" />
                </button>
              ))}
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
